import {
  WSCard,
  WSElement,
  WSFlexBox,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { PayStatementTaxEntry } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { PAYROLL_CURRENCY } from "../../constants";
import { parsePayrollAmount } from "../../utils";
import {
  StatementColumn,
  StatementRow,
  StatementTable
} from "../StatementTable";

const DEDUCTION_COLUMNS: StatementColumn[] = [
  { span: "6" },
  { span: "3", align: "end" },
  { span: "3", align: "end" }
];

type Props = {
  /**
   * The worker's tax withholdings. v3 models them as one flat list (`amount` +
   * `description`) with no pre-tax/per-state split, so they render as a single
   * "Withholdings" group. Employer contributions (`payerTaxes`) are the
   * employer's cost, not deducted from the worker, and are not shown here.
   */
  payeeTaxes: PayStatementTaxEntry[];
  deductionTotalThisPeriod: number;
  deductionTotalYearToDate: number;
  netPayThisPeriod: number;
  netPayYearToDate: number;
  payoutMethodLabel: string;
};

export const PaystubDeductionsCard: React.FC<Props> = ({
  payeeTaxes,
  deductionTotalThisPeriod,
  deductionTotalYearToDate,
  netPayThisPeriod,
  netPayYearToDate,
  payoutMethodLabel
}) => {
  const rows: StatementRow[] = [];

  if (payeeTaxes.length) {
    rows.push({
      id: "withholdings-header",
      variant: "header",
      cells: ["Withholdings", "This period", "Year to date"]
    });

    payeeTaxes.forEach((tax, index) => {
      rows.push({
        id: `withholding-${index}`,
        variant: "item",
        cells: [
          tax.description ?? "Withholding",
          formatMoney(parsePayrollAmount(tax.amount), PAYROLL_CURRENCY),
          // Only whole-statement YTD exists in v3 (`amountDetails.ytd`), never
          // per withholding line.
          formatMoney(0, PAYROLL_CURRENCY)
        ]
      });
    });
  }

  rows.push({
    id: "deduction-total",
    variant: "subtotal",
    cells: [
      "Deduction total",
      formatMoney(deductionTotalThisPeriod, PAYROLL_CURRENCY),
      formatMoney(deductionTotalYearToDate, PAYROLL_CURRENCY)
    ]
  });

  rows.push({
    id: "net-pay",
    variant: "net",
    cells: [
      <WSFlexBox.CenterY key="net-pay-label">
        <WSText.ParagraphSm color="gray700" weight="medium">
          Net pay
        </WSText.ParagraphSm>
        <WSText.ParagraphSm color="gray500" ml="XS">
          {payoutMethodLabel}
        </WSText.ParagraphSm>
      </WSFlexBox.CenterY>,
      formatMoney(netPayThisPeriod, PAYROLL_CURRENCY),
      formatMoney(netPayYearToDate, PAYROLL_CURRENCY)
    ]
  });

  return (
    <WSElement>
      <WSSectionToolbar title="Deductions" />
      <WSCard mt="M">
        <StatementTable columns={DEDUCTION_COLUMNS} rows={rows} />
      </WSCard>
    </WSElement>
  );
};
